import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TableSkeleton } from '@/lib/components';
import { Spinner } from '@/lib/components/Util/Spinner';
import {
  Card,
  CardSection,
  CleanInputSearchRef,
  Container,
  FloatModal,
  IconSvg,
  InputSearch,
  toast,
} from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import { SegmentsIcon } from '@/lib/v2/icons/solid';

import { EventListenerType } from '@/src/application/hooks/enums/EventListenerEnum';
import useMessage from '@/src/compat/useMessage';
import { DeleteModal } from '@/src/ContactsModule/components/ContactsModals';
import { DuplicateModal } from '@/src/ContactsModule/components/ContactsModals/DuplicateModal';
import { SegmentsActionBar } from '@/src/ContactsModule/components/SegmentsActionBar/SegmentsActionBar';
import { SegmentsSwitcherEnum } from '@/src/ContactsModule/utils/enums/SegmentsSwitcher';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import {
  useMutationContactsContext,
  useStateContactsContext,
} from '@/src/modules/ContactsModule/contexts/ContactsContext';
import { useMutationCreateSegmentModalContext } from '@/src/modules/ContactsModule/contexts/CreateSegmentModalContext';
import CardsFilterSegments from '@/src/modules/ContactsModule/screens/CreateSegment/components/CardsFilterSegments/CardsFilterSegments';

import { CreateEmblueSegmentModal } from './components/CreateEmblueSegment/CreateEmblueSegmentModal';
import { useRecalculateSegment } from './useRecalculateSegment';

import { DistributeModal, SectionHeader } from '@/modules/ContactsModule/components';
import { useDistributeContacts } from '@/modules/ContactsModule/hooks/useDistributeContacts';
import { useSegmentMain } from '@/modules/ContactsModule/hooks/useSegmentMain';
import {
  CreateSegmentButton,
  SegmentsTable,
  SegmentTitle,
} from '@/modules/ContactsModule/screens/SegmentsMain/components';

type SegmentsSwitcherType = SegmentsSwitcherEnum;

export const SegmentsMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // action bar states
  const [selectedSegmentsRows, setSelectedSegmentsRows] = useState<number[]>([]);
  const [isChecked, setIsChecked] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  // END action bar states

  const {
    searchQuery: search,
    pageSize,
    pageIndex,
    totalPages,
    gotoPageIndex,
  } = useStateContactsContext();
  const { setSearchQuery, changeTablePage, resetPagination, setListTotalPagination } =
    useMutationContactsContext();

  const cleanBoxRef = useRef<CleanInputSearchRef>(null);
  const [isRecalculateSegment, setIsRecalculateSegment] = useState(false);

  const [lastRefresh, setLastRefresh] = useState<number>(0);

  const [showCreateEmblueSegmentModal, setShowCreateEmblueSegmentModal] = useState<boolean>(false);
  const { setCreateSegmentModal } = useMutationCreateSegmentModalContext();

  const [segmentsSwitcher, setSegmentsSwitcher] = useState<SegmentsSwitcherType>(
    (sessionStorage.getItem('segmentType') as SegmentsSwitcherType) ??
      SegmentsSwitcherEnum.Customized
  );

  // services
  const segmentType = useMemo(() => {
    let numberType;
    if (SegmentsSwitcherEnum.Customized === segmentsSwitcher) {
      numberType = 0;
    } else if (SegmentsSwitcherEnum.EmBlueRecipes === segmentsSwitcher) {
      numberType = 1;
    } else {
      numberType = 2;
    }

    return numberType;
  }, [segmentsSwitcher]);

  const {
    segmentList,
    isLoadingSegments,
    isLoadingSegmentCountTotal,
    segmentsCountOfSearch,
    segmentsCountTotal,
    emblueSegmentTotalsCard,
    setOrderColumn,
    setOrderDirection,
  } = useSegmentMain({
    lastRefresh,
    segmentTypes: [segmentType],
    search,
    page: pageIndex,
    pageSize,
  });

  const {
    distributeIsLoading,
    handleDistributeSubmit,
    distributeIsDisabled,
    openDistributeModal,
    setOpenDistributeModal,
    quantityOptions,
  } = useDistributeContacts({
    currentList: segmentList?.list ?? [],
    selectedRows: selectedSegmentsRows,
  });

  const service = useService();

  const lastRefreshIncrement = useCallback(() => {
    setLastRefresh((prevState: number) => prevState + 1);
    service?.incrementLastRefreshSegmentsValue();
  }, [service]);

  let queryParamsFilters = '';

  if (search) {
    queryParamsFilters += `filterSearch=${search}`;
  }

  sessionStorage.setItem('queryParamsFilters', queryParamsFilters);

  useEffect(() => {
    setListTotalPagination(segmentsCountOfSearch?.count ?? 0);
  }, [segmentsCountOfSearch?.count, setListTotalPagination]);

  //Remove this when create segment V2 its on production
  useMessage(EventListenerType.RefreshSegments, () => {
    lastRefreshIncrement();
  });

  const [recalculateSegment, recalculateSegmentResponse] = useRecalculateSegment();

  const createSegment = useCallback(() => {
    setShowCreateEmblueSegmentModal(true);
  }, []);

  useEffect(() => {
    if (recalculateSegmentResponse) {
      setIsRecalculateSegment(false);
      lastRefreshIncrement();
    }
  }, [lastRefreshIncrement, recalculateSegmentResponse]);

  const recalculateSegmentHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>, segmentId: number) => {
      event.stopPropagation();
      if (segmentId) {
        setIsRecalculateSegment(true);
        recalculateSegment(segmentId, lastRefresh);
      }
    },
    [recalculateSegment, lastRefresh]
  );

  const changeOrderBy = useCallback(
    (element: { id: string; isAsc: boolean | undefined }) => {
      const orderDirectionValue = element.isAsc === true ? 'desc' : 'asc';
      setOrderDirection(orderDirectionValue);
      setOrderColumn(element.id);
    },
    [setOrderColumn, setOrderDirection]
  );

  const goToSegmentDetail = useCallback(
    (segmentId: number) => {
      navigate(`../segments/${segmentId}`);
    },
    [navigate]
  );

  const resetSegments = useCallback(() => {
    setSearchQuery('');
    resetPagination();
    cleanBoxRef.current?.cleanInputSearch();
  }, [resetPagination, setSearchQuery]);

  const handleActionInputSearch = useCallback(
    (value: string) => {
      resetPagination();
      setSearchQuery(value);
    },
    [resetPagination, setSearchQuery]
  );

  useEffect(() => {
    setIsChecked(selectedSegmentsRows.length > 0);
  }, [selectedSegmentsRows]);

  const deleteSegments = useCallback(async () => {
    const currentSegmentList = segmentList?.list;
    if (currentSegmentList === undefined || selectedSegmentsRows.length < 1) return;
    // spinner on
    setShowSpinner(true);
    const segmentsIds: number[] =
      selectedSegmentsRows.length === pageSize
        ? currentSegmentList.map((segment) => segment.segment.id) // all ids of page
        : currentSegmentList
            .filter((s, i) => selectedSegmentsRows.includes(i))
            .map((segment) => segment.segment.id); // ids of some contacts in page
    const result = await service?.deleteSegments({ ids: segmentsIds });
    // spinner off
    setShowSpinner(false);
    toast({
      title: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkTitle')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertTitle'),
      body: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkBody')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertBody'),
      variant: result ? 'success' : 'error',
    });
  }, [pageSize, segmentList?.list, selectedSegmentsRows, service, t]);

  useEffect(() => {
    if (confirmDelete) {
      deleteSegments()
        .then(() => lastRefreshIncrement())
        .catch(console.error);
      setConfirmDelete(false);
    }
  }, [confirmDelete, deleteSegments, lastRefreshIncrement]);

  const duplicateSegments = useCallback(async () => {
    const currentSegmentList = segmentList?.list;
    if (currentSegmentList === undefined || selectedSegmentsRows.length < 1) return;
    // spinner on
    setShowSpinner(true);
    const segment = currentSegmentList.filter((s, i) => selectedSegmentsRows.includes(i));
    const result = await service?.duplicateSegment({
      id: segment[0].segment.id,
      name: `${segment[0].segment.name} ${t('duplicate')}`,
    });
    // spinner off
    setShowSpinner(false);
    if (result?.success) {
      toast({
        variant: 'success',
        title: t('CREATE_SEGMENT.success'),
        body: t('CREATE_SEGMENT.bodyMessageSuccess'),
        autoCloseDelay: 5000,
      });
      goToSegmentDetail(result.data.segmentId);
    } else {
      toast({
        variant: 'error',
        title: t('CREATE_SEGMENT.error'),
        body: result?.data.errorByName
          ? t('EMBLUE_SEGMENTS.Notification.ERROR.bodyRepeatedName')
          : t('CREATE_SEGMENT.bodyMessageError'),
        autoCloseDelay: 5000,
      });
    }
  }, [goToSegmentDetail, segmentList?.list, selectedSegmentsRows, service, t]);

  useEffect(() => {
    if (confirmDuplicate) {
      duplicateSegments()
        .then(() => lastRefreshIncrement())
        .catch(console.error);
      setConfirmDuplicate(false);
    }
  }, [confirmDuplicate, duplicateSegments, lastRefreshIncrement]);

  const handleActionCardsFilters = useCallback(
    (value: string) => {
      resetSegments();
      setSegmentsSwitcher(value as SegmentsSwitcherType);

      sessionStorage.removeItem('segmentType');
    },
    [resetSegments]
  );

  return (
    <>
      <CardsFilterSegments
        emblueSegments={emblueSegmentTotalsCard}
        onAction={handleActionCardsFilters}
      />
      <Card>
        <CardSection noPadding>
          <Flex column>
            <SectionHeader
              actionsButtons={<CreateSegmentButton createSegment={createSegment} />}
              icon={<IconSvg height="30px" svgComponent={<SegmentsIcon />} width="30px" />}
              justifyContentActionsButtons="end"
              title={
                <SegmentTitle
                  isLoadingSegmentCountTotal={isLoadingSegmentCountTotal}
                  isLoadingSegments={isLoadingSegments}
                  search={search ?? ''}
                  segmentListLength={segmentList?.list.length ?? 0}
                  segmentsCountOfSearch={segmentsCountOfSearch?.count ?? 0}
                  segmentsCountTotal={segmentsCountTotal?.count ?? 0}
                  segmentsSwitcher={segmentsSwitcher}
                />
              }
            />
            <Flex column withGap className="mt-7" gapSize="medium">
              {!isChecked ? (
                <Flex
                  alignment="start"
                  className={`mb-[-62px] ${
                    !isChecked && 'xl:max-w-[900px] 2xl:max-w-fit'
                  } self-start`}
                >
                  <Container>
                    {(segmentsCountTotal ? segmentsCountTotal?.count : 0) > 0 && (
                      <div className="w-[340px]">
                        <InputSearch
                          ref={cleanBoxRef}
                          defaultValue={search}
                          placeHolder={t('SEGMENTS_FILTERS.searchInputBar')}
                          onAction={handleActionInputSearch}
                          onClear={resetSegments}
                        />
                      </div>
                    )}
                  </Container>
                </Flex>
              ) : (
                <Flex alignment="start" className="z-50 mb-[-62px] self-start">
                  <SegmentsActionBar
                    contactSelection={selectedSegmentsRows.length}
                    distributeIsDisabled={distributeIsDisabled}
                    segmentsSwitcher={segmentsSwitcher}
                    setOpenDeleteModal={setOpenDeleteModal}
                    setOpenDistributeModal={setOpenDistributeModal}
                    setOpenDuplicateModal={setOpenDuplicateModal}
                  />
                </Flex>
              )}
              {isLoadingSegments && (
                <div className="h-full w-full py-10">
                  <TableSkeleton />
                </div>
              )}
              {segmentsSwitcher && segmentList && (
                <SegmentsTable
                  callToAction={[resetSegments, createSegment]}
                  changeTableOrder={changeOrderBy}
                  changeTablePage={changeTablePage}
                  gotoPageIndex={gotoPageIndex}
                  isLoading={isLoadingSegments}
                  isRecalculateSegment={isRecalculateSegment}
                  pageSize={pageSize}
                  recalculateSegment={recalculateSegmentHandler}
                  segmentList={segmentList.list}
                  segmentsSwitcher={segmentsSwitcher}
                  setSelectedSegments={setSelectedSegmentsRows}
                  totalPages={totalPages}
                  totalSegmentCount={segmentsCountTotal?.count ?? 0}
                />
              )}
            </Flex>
          </Flex>
        </CardSection>
      </Card>
      <CreateEmblueSegmentModal
        isShow={showCreateEmblueSegmentModal}
        lastRefreshIncrement={lastRefreshIncrement}
        setCreateCustomSegment={setCreateSegmentModal}
        setIsLoading={setShowSpinner}
        onClose={setShowCreateEmblueSegmentModal}
      />
      <DeleteModal
        description={
          selectedSegmentsRows.length === 1
            ? t('SEGMENT_DELETE_MODAL.description')
            : t('SEGMENT_DELETE_MODAL.descriptionPlural')
        }
        metadata={{ entity: 'segment' }}
        setConfirmDelete={setConfirmDelete}
        title={
          selectedSegmentsRows.length === 1
            ? t('SEGMENT_DELETE_MODAL.title')
            : t('SEGMENT_DELETE_MODAL.titlePlural')
        }
        v2Modal={{ showModal: openDeleteModal, setShowModal: setOpenDeleteModal }}
      />
      <DuplicateModal
        description={t('SEGMENT_DUPLICATE_MODAL.description')}
        metadata={{ entity: 'segment' }}
        setConfirmDuplicate={setConfirmDuplicate}
        title={t('SEGMENT_DUPLICATE_MODAL.title')}
        v2Modal={{ showModal: openDuplicateModal, setShowModal: setOpenDuplicateModal }}
      />
      <DistributeModal
        isLoading={distributeIsLoading}
        isOpen={openDistributeModal}
        quantityOptions={quantityOptions}
        onClose={setOpenDistributeModal}
        onSubmit={handleDistributeSubmit}
      />
      {showSpinner && (
        <FloatModal>
          <Spinner />
        </FloatModal>
      )}
    </>
  );
};
